/* eslint-disable max-lines */
import combineLineGeometry from '@gaia/utils/gaia/combineLineGeometry';
import isOSMTrailSymbol from '@gaia/utils/gaia/isOSMTrailSymbol';
import isOSMUnnamed from '@gaia/utils/gaia/isOSMUnnamed';
import type {Feature, Geometry} from '@turf/helpers';
import flattenDeep from 'lodash/flattenDeep';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import type {GeoJSONSourceRaw, SymbolLayout} from 'mapbox-gl';

import {activeLineLayers, selectionGreen, selectionWhite} from './constants';
import {osmSelectionFixedLayerIds} from './fixedLayerIds';

export default function getSelectionsMapboxStyle(
    getStyle: MapGetStyle,
    querySourceFeatures: MapQuerySourceFeatures,
    selection: SelectionState
): mapboxgl.Style | undefined {
    if (!querySourceFeatures || !getStyle) return undefined;

    let osmSelection: OSMSelectionState;

    if (selection.type === 'osm' && selection.properties.geometry) {
        osmSelection = selection;
    } else if (
        selection.type === 'knownRoute' &&
        selection.previousSelection &&
        selection.previousSelection.type === 'osm'
    ) {
        osmSelection = selection.previousSelection;
    } else if (selection.type === 'osm' && selection.properties.type === 'poi') {
        // POI Search Results don't contain required info to render symbol & title in bold
        osmSelection = {
            ...selection,
            mapboxLayerId: '__poi-bold',
            properties: {
                ...selection.properties,
                geometry: {
                    type: 'Point',
                    coordinates: selection.properties.centroid,
                } as GeoJSON.Point,
            },
        };
    } else {
        return undefined;
    }

    if (osmSelection.properties?.type === 'land') {
        return landSelectionHighlightStyle(osmSelection);
    }
    const {mapboxLayerId} = osmSelection;
    if (!mapboxLayerId) return undefined;

    // often OSM trails are made up of their "Nearby Known Routes".
    // highlighting both makes it hard to see which is which
    if (!isPOI(osmSelection) && osmSelection.hoverKnownRouteId) return undefined;

    let mapLayers;
    try {
        mapLayers = getStyle().layers;
    } catch (e) {
        return undefined;
    }

    if (!mapLayers) return undefined;

    const layer: mapboxgl.Layer | undefined = mapLayers.find((l) => l.id.includes(mapboxLayerId));
    if (!layer) return undefined;

    let source: GeoJSONSourceRaw = {
        type: 'geojson',
        data: {
            type: 'Feature',
            geometry: osmSelection.properties.geometry,
            properties: osmSelection.properties,
        },
    };

    // If layer source is not a string, then it is out of the jurisdiction of this style.
    if (!isString(layer.source)) {
        return undefined;
    }

    let layers: (mapboxgl.LineLayer | mapboxgl.SymbolLayer)[] | undefined;
    // Some well-known trails have on-map symbols next to them (e.g. PCT) or display segments with mileage labels
    // For these, the trail geometry should be highlighted instead of the symbol
    if (
        layer.type === 'line' ||
        (layer.type === 'symbol' && isOSMTrailSymbol(osmSelection.mapboxLayerId))
    ) {
        if (layer.source && !isOSMUnnamed(osmSelection.properties)) {
            // source in selection is bound by its vector tile; this queries all tiles on map
            const sourceFeatures = querySourceFeatures(layer.source, {
                sourceLayer: layer['source-layer'],
                filter: ['==', ['get', 'name'], osmSelection.title],
            });
            source = combineLineGeometry(sourceFeatures);
        }
        layers = activeLineLayers(
            osmSelectionFixedLayerIds.line,
            osmSelectionFixedLayerIds.lineShadow,
            'source'
        );
    } else if (layer.type === 'symbol') {
        if (!(source.data as Feature<Geometry, any>).properties?.symbol) {
            // Search results don't contain the POI symbol to highlight.
            // Use search result data to query map:
            const sourceFeatures = querySourceFeatures(layer.source, {
                sourceLayer: layer['source-layer'],
                filter: ['==', ['get', 'name'], osmSelection.title],
            });
            if (
                sourceFeatures.length > 0 &&
                !isString(source.data) &&
                source.data?.type === 'Feature'
            ) {
                // If there are multiple source features, find the one whose type most closely matches the search result.
                // This ensures the search result, sidebar icon, and map symbol all match.
                const feature =
                    sourceFeatures.find(
                        (f) => f.properties?.type === osmSelection.properties.subtype
                    ) ?? sourceFeatures[0];
                source.data.properties = {
                    ...source.data.properties,
                    ...feature.properties,
                };
            } else {
                return undefined;
            }
        }
        layers = osmSymbolSelectionLayers(layer);
    }

    return {
        version: 8,
        metadata: {id: 'selectionStyle'},
        sources: {source},
        layers,
    };
}

function isPOI(osmSelection: OSMSelectionState) {
    return osmSelection.properties.geometry?.type === 'Point';
}

function osmSymbolSelectionLayers(
    sourceLayer: mapboxgl.Layer
): [mapboxgl.SymbolLayer, mapboxgl.SymbolLayer] {
    const textFieldLayout = (sourceLayer.layout as SymbolLayout)['text-field'];
    const textFontConfig = isArray(textFieldLayout)
        ? flattenDeep(textFieldLayout).find(
              (x) => isObject(x) && (x as Record<string, any>)['text-font']
          )
        : undefined;
    const isMetric = sourceLayer.id.includes('Meters');
    const usesSquareFont = textFontConfig
        ? textFontConfig['text-font']?.[1]?.[0]?.includes('Square')
        : false;

    const textOffset = (sourceLayer.layout as SymbolLayout)['text-offset'] ?? [0, 0];

    const hasSymbolAndNextLineExpression = [
        'all',
        ['has', 'symbol'],
        ['any', ['has', 'name'], ['has', 'ele_feet']],
    ];
    const hasNameAndNextLineExpression = ['all', ['has', 'name'], ['has', 'ele_feet']];

    return [
        {
            type: 'symbol',
            id: osmSelectionFixedLayerIds.symbolShadow,
            maxzoom: 24,
            minzoom: 0,
            source: 'source',
            layout: {
                'symbol-placement': 'point',
                'text-field': [
                    'format',
                    ['get', 'symbol'],
                    {
                        'text-font': [
                            'literal',
                            [
                                usesSquareFont
                                    ? 'GaiaTopoRasterFeet__GaiaSquare_v3'
                                    : 'GaiaTopoRasterFeet__GaiaFont_v3',
                            ],
                        ],
                        'font-scale': 1,
                    },
                    ['case', hasSymbolAndNextLineExpression, '\n', ''],
                    ['get', 'name'],
                    {
                        'text-font': ['literal', ['GaiaTopoRasterFeet__Open_Sans-BoldItalic']],
                        'font-scale': 0.9,
                    },
                    ['case', hasNameAndNextLineExpression, '\n', ''],
                    ['get', isMetric ? 'ele_meters' : 'ele_feet'],
                    {
                        'text-font': ['literal', ['GaiaTopoRasterFeet__Open_Sans-BoldItalic']],
                        'font-scale': 0.8,
                    },
                ],
                'text-justify': 'center',
                'text-size': 16,
                'text-font': ['GaiaTopoRasterFeet__Open_Sans-BoldItalic'],
                'text-letter-spacing': 0,
                'icon-size': {
                    stops: [
                        [7, 0.5],
                        [10, 0.5],
                        [16, 0.7],
                    ],
                },
                'text-max-width': {
                    stops: [
                        [10, 2],
                        [12, 5],
                    ],
                },
                'text-line-height': 1.1,
                'text-offset': textOffset,
            },
            paint: {
                'text-color': selectionGreen,
                'text-halo-color': selectionGreen,
                'text-halo-width': 1.8,
                'icon-opacity': 1,
                'text-opacity': 1,
                'text-halo-blur': 1,
                'text-translate': [1.5, 1.5],
            },
        },
        {
            type: 'symbol',
            id: osmSelectionFixedLayerIds.symbol,
            maxzoom: 24,
            minzoom: 0,
            source: 'source',
            layout: {
                'text-field': [
                    'format',
                    ['get', 'symbol'],
                    {
                        'text-font': [
                            'literal',
                            [
                                usesSquareFont
                                    ? 'GaiaTopoRasterFeet__GaiaSquare_v3'
                                    : 'GaiaTopoRasterFeet__GaiaFont_v3',
                            ],
                        ],
                        'font-scale': 1,
                    },
                    ['case', hasSymbolAndNextLineExpression, '\n', ''],
                    ['get', 'name'],
                    {
                        'text-font': ['literal', ['GaiaTopoRasterFeet__Open_Sans-BoldItalic']],
                        'font-scale': 0.9,
                    },
                    ['case', hasNameAndNextLineExpression, '\n', ''],
                    ['get', isMetric ? 'ele_meters' : 'ele_feet'],
                    {
                        'text-font': ['literal', ['GaiaTopoRasterFeet__Open_Sans-BoldItalic']],
                        'font-scale': 0.8,
                    },
                ],
                'text-justify': 'center',
                'text-size': 16,
                'text-font': ['GaiaTopoRasterFeet__Open_Sans-BoldItalic'],
                'text-letter-spacing': 0,
                'icon-size': {
                    stops: [
                        [7, 0.5],
                        [10, 0.5],
                        [16, 0.7],
                    ],
                },
                'text-max-width': {
                    stops: [
                        [10, 2],
                        [12, 5],
                    ],
                },
                'text-line-height': 1.1,
                'text-offset': textOffset,
            },
            paint: {
                'text-color': selectionWhite,
                'text-halo-color': selectionGreen,
                'text-halo-width': 1.8,
                'icon-opacity': 1,
                'text-opacity': 1,
                'text-halo-blur': 0,
                'text-translate': [0, 0],
            },
        },
    ];
}

function landSelectionHighlightStyle(landData: OSMSelectionState): mapboxgl.Style | undefined {
    if (!landData.properties.geometry) return undefined;

    return {
        version: 8,
        metadata: {id: 'selectionStyle'},
        sources: {
            source: {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    geometry: landData.properties.geometry as GeoJSON.Polygon,
                    properties: landData.properties,
                },
            },
        },
        layers: landSelectionLayers(),
    };
}

function landSelectionLayers(): [mapboxgl.LineLayer, mapboxgl.FillLayer, mapboxgl.LineLayer] {
    return [
        {
            id: osmSelectionFixedLayerIds.landShadow,
            type: 'line',
            source: 'source',
            layout: {
                'line-join': 'round',
                'line-cap': 'round',
                'line-miter-limit': 2,
                'line-round-limit': 1.05,
            },
            paint: {
                'line-width': {
                    stops: [
                        [9, 5],
                        [12, 8],
                    ],
                },
                'line-offset': 0,
                'line-color': 'rgba(67, 135, 48, 1)',
                'line-opacity': 0.6,
                'line-blur': {
                    stops: [
                        [9, 2],
                        [12, 3],
                    ],
                },
                'line-translate': {
                    stops: [
                        [9, [1, 1]],
                        [12, [2, 2]],
                    ],
                } as unknown as any,
            },
            minzoom: 0,
            maxzoom: 24,
        },
        {
            id: osmSelectionFixedLayerIds.landFill,
            type: 'fill',
            source: 'source',
            paint: {
                'fill-color': 'rgba(255, 255, 255,1)',
                'fill-opacity': 0.5,
                'fill-antialias': false,
            },
            minzoom: 5,
            maxzoom: 24,
        },
        {
            id: osmSelectionFixedLayerIds.landLine,
            type: 'line',
            source: 'source',
            paint: {
                'line-color': 'rgba(36, 86, 23, 0.6)',
                'line-width': {
                    stops: [
                        [9, 1],
                        [12, 1.5],
                    ],
                },
            },
            minzoom: 5,
            maxzoom: 24,
        },
    ];
}
